import { useEffect, useRef } from 'react';

interface UseIdleTimeoutProps {
  onIdle: () => void;
  idleTime?: number; // in milliseconds
  enabled?: boolean;
}

export const useIdleTimeout = ({ 
  onIdle, 
  idleTime = 5 * 60 * 1000, // 5 minutes default
  enabled = true 
}: UseIdleTimeoutProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = () => {
    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timer
    if (enabled) {
      timeoutRef.current = setTimeout(() => {
        onIdle();
      }, idleTime);
    }
  };

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any activity
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [onIdle, idleTime, enabled]);

  return { resetTimer };
};
